import { LocationSearchType } from "@/gen/ts/v1/user/user_service_pb";
import { z } from "zod";

/**
 * Core location preferences interface
 */
export interface LocationPreferences {
  locationType: "community" | "beyond";
  postalCode?: string;
  radiusKm: number;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

/**
 * Extended location preferences with computed fields
 * These fields are computed on the frontend and not stored in the database
 */
export interface ExtendedLocationPreferences extends LocationPreferences {
  locationName?: string;
  city?: string; // Frontend-only computed field
  state?: string; // Frontend-only computed field
  locationNameWithState?: string; // Frontend-only computed field
}

/**
 * Parameters for getting location preferences
 */
export interface GetLocationPrefsParams {
  userId?: string;
}

/**
 * Parameters for updating location preferences
 */
export const updateLocationPrefsSchema = z.discriminatedUnion("locationType", [
  z.object({
    locationType: z.literal("community"),
    postalCode: z.string(),
    radiusKm: z.number(),
    coordinates: z.object({
      latitude: z.number(),
      longitude: z.number(),
    }),
  }),
  z.object({
    locationType: z.literal("beyond"),
  }),
]);

export type UpdateLocationPrefsInput = z.infer<
  typeof updateLocationPrefsSchema
>;

/**
 * Default location preferences
 */
export const DEFAULT_PREFERENCES: LocationPreferences = {
  locationType: "community",
  radiusKm: 30,
};

/**
 * Local storage key for preferences
 */
export const STORAGE_KEY = "local-search-location";

/**
 * Generate a cache key for React Query based on user ID
 */
export function getLocationPrefsQueryKey(userId?: string): string[] {
  return ["location-preferences", userId ?? "anon"];
}

/**
 * Convert location preferences to proto enum
 */
export function locationTypeToProto(
  locationType: "community" | "beyond",
): LocationSearchType {
  return locationType === "community"
    ? LocationSearchType.COMMUNITY
    : LocationSearchType.BEYOND;
}

/**
 * Convert proto enum to location preferences
 */
export function protoToLocationType(
  searchType: LocationSearchType,
): "community" | "beyond" {
  return searchType === LocationSearchType.COMMUNITY ? "community" : "beyond";
}
